import React, { useState, useEffect } from 'react';
import { enquiriesApi } from '../modules/enquiries/api/enquiriesApi';
import { dealsApi } from './sales-pipeline/api/deals';

const getCurrentEcode = () => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return '';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub || payload.ecode || '';
  } catch {
    return '';
  }
};

const LocalQuotations = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const [previewRow, setPreviewRow] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  const loadRows = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await enquiriesApi.getAllEnquiries({ limit: 500 });
      const sorted = [...data].sort((a, b) => {
        const da = a.EnquiryDate || a.enquiry_date || '';
        const db = b.EnquiryDate || b.enquiry_date || '';
        return db.localeCompare(da);
      });
      setRows(sorted);
    } catch (e) {
      console.error('Failed to load local quotations:', e);
      setError(e.message || 'Failed to load local quotations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRows = rows.filter((r) => {
    if (filter === 'approved') return !!r.approved_by_name;
    if (filter === 'pending') return !r.approved_by_name;
    return true;
  });

  const approvedCount = rows.filter((r) => r.approved_by_name).length;
  const pendingCount = rows.length - approvedCount;

  const openPreview = async (row) => {
    setPreviewRow(row);
    setPreviewPdfUrl(null);
    setPreviewError('');
    if (!row.deal_id) {
      setPreviewError('This enquiry has no linked deal yet, so no offer PDF exists to preview.');
      return;
    }
    setIsLoadingPreview(true);
    try {
      const { blob } = await dealsApi.downloadOfferLetter(row.deal_id);
      const url = window.URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
    } catch (e) {
      setPreviewError(e.message || 'Failed to load the offer PDF');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const closePreview = () => {
    if (previewPdfUrl) window.URL.revokeObjectURL(previewPdfUrl);
    setPreviewRow(null);
    setPreviewPdfUrl(null);
    setPreviewError('');
  };

  const handleApprove = async () => {
    if (!previewRow) return;
    setIsApproving(true);
    try {
      const ecode = getCurrentEcode();
      await enquiriesApi.updateEnquiry(previewRow.id, { approved_by_ecode: ecode });
      await loadRows();
      closePreview();
    } catch (e) {
      setPreviewError(e.message || 'Failed to approve this quotation');
    } finally {
      setIsApproving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Local Quotations</h1>
        <p className="text-sm text-gray-500 mt-1">
          Every local offer, across all sales coordinators — preview the offer PDF and approve right from here.
        </p>
      </div>

      <div className="flex gap-3 mb-4">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 text-sm rounded-md border ${filter === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
        >
          All ({rows.length})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-3 py-1.5 text-sm rounded-md border ${filter === 'pending' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
        >
          Pending Approval ({pendingCount})
        </button>
        <button
          onClick={() => setFilter('approved')}
          className={`px-3 py-1.5 text-sm rounded-md border ${filter === 'approved' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
        >
          Approved ({approvedCount})
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Customer</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">SC</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Offer No</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Approved By</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600"></th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-900">{r.AccountName || '-'}</td>
                  <td className="px-4 py-3 text-gray-700">{r.OwnerName || r.OwnerECode || '-'}</td>
                  <td className="px-4 py-3 text-gray-700">{r.offer_no || '-'}</td>
                  <td className="px-4 py-3 text-gray-700">{r.DerivedStatus || r.Status || '-'}</td>
                  <td className="px-4 py-3">
                    {r.approved_by_name ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                        {r.approved_by_name}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{r.EnquiryDate || '-'}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => openPreview(r)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Preview &amp; Approve
                    </button>
                  </td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                    No quotations found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {previewRow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  {previewRow.AccountName || 'Offer preview'}
                </h2>
                <p className="text-xs text-gray-500">{previewRow.offer_no || `Enquiry #${previewRow.id}`}</p>
              </div>
              <button onClick={closePreview} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>

            <div className="flex-1 overflow-auto bg-gray-100">
              {isLoadingPreview && (
                <p className="text-sm text-gray-500 p-6 text-center">Loading PDF…</p>
              )}
              {previewError && (
                <p className="text-sm text-red-600 p-6 text-center">{previewError}</p>
              )}
              {previewPdfUrl && !isLoadingPreview && (
                <iframe
                  title="Offer PDF preview"
                  src={previewPdfUrl}
                  className="w-full h-full min-h-[60vh]"
                />
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-200">
              <button
                onClick={closePreview}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={handleApprove}
                disabled={isApproving || !previewRow.deal_id || !!previewRow.approved_by_name}
                className="px-4 py-1.5 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {previewRow.approved_by_name ? 'Already Approved' : isApproving ? 'Approving…' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalQuotations;
